import { CheckmarkFilled, CheckmarkFilledWarning, Incomplete } from "@carbon/icons-react";

import { DynamicViewData } from "src/dynamic-view-renderer/DynamicViewRenderer.typings";
import { useT } from "src/internationalization";
import { Case } from "src/types/case";

import {
  getComparison,
  getConfiguredElements,
  MappedCaseObject,
  useMapCaseObject,
} from "../Widgets/RelatedCases/relatedCases.mappings";
import { RelatedWidgetConfiguration } from "../Widgets/RelatedCases/relatedCases.typings";

export function PrintableRelatedCasesWidget({
  data,
  configuration,
  maxRelatedCases,
}: {
  data: DynamicViewData["widgetsData"]["relatedCases"];
  configuration: RelatedWidgetConfiguration;
  maxRelatedCases: number;
}) {
  const mapCase = useMapCaseObject();
  const t = useT();

  const cases = data && data.cases && data.cases.slice(0, maxRelatedCases);
  const total = data && (data.totalCount || (data.cases && data.cases.length));
  if (total === 0) return <p>{t("page.alert.pdf.relatedcases.none")}.</p>;
  if (!cases || !total) return <p>Les demande lié ne sont pas definie.</p>;

  type caseColumns = {
    label: string;

    render: (row: MappedCaseObject) => string;
  };

  type matchColumns = {
    label: string;
    _label?: string;

    render: (row: MappedCaseObject) => React.ReactNode;
  };

  const caseCols: [string, caseColumns][] = configuration.caseColumns.map((column) => [
    column?.code ? column.code : (column._label ?? column.label),
    {
      label: column.label,
      render: (row: MappedCaseObject) =>
        typeof row[column._label ?? column.label] === "string"
          ? String(row[column._label ?? column.label])
          : "-",
    },
  ]);

  const matchCols: [string, matchColumns][] = (configuration.matchElementsColumns ?? [])
    .map((column) => {
      return {
        label: column.label,
        _label: column._label,
        render: (mappedValue: MappedCaseObject) => {
          const caseData = mappedValue.row as Case;
          const comparison = getComparison(
            caseData,
            column,
            getConfiguredElements(configuration, data && data.elements)
          );

          switch (comparison?.match) {
            case "Full":
              return <CheckmarkFilled className="text-supportSuccessInverse" />;

            case "Partial":
              return <Incomplete />;

            case "Cross":
              return <CheckmarkFilledWarning className="text-supportError" />;

            default:
              return <div className="text-textSecondary">-</div>;
          }
        },
      };
    })
    .map((col) => [col._label ?? col.label, col]);

  const columns: Record<string, matchColumns | caseColumns> = Object.fromEntries([
    ...caseCols,
    ...matchCols,
  ]);

  return (
    <table className="printable-related-cases w-full table-fixed border-collapse text-label01 leading-body01">
      <caption className="text-left pb-2 text-textSecondary">
        {cases.length}{" "}
        {cases.length === 1 ? t("page.alert.pdf.relatedCase") : t("page.alert.pdf.relatedCases")}{" "}
        {total}
        {total > cases.length && ` · ${t("page.alert.pdf.report.limit")} : ${maxRelatedCases}`}
      </caption>
      <thead>
        <tr>
          {Object.keys(columns).map((column, index) => (
            <th
              scope="col"
              key={index}
              className="py-3 px-3 text-left align-top  border-b border-solid font-semibold border-b-borderSubtle bg-layer01 "
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cases.map((relatedCase) => {
          const row = mapCase(relatedCase, configuration);

          return (
            <tr key={relatedCase.id}>
              {Object.values(columns).map((value, index) => (
                <td
                  key={index}
                  className="py-3 px-3 text-left align-top border-b border-solid break-words border-b-borderSubtle"
                >
                  {row ? value.render(row) : "—"}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
